// Shared field value extraction for Jira issues
// Used by Structure.jsx (table columns) and GanttPanel.jsx (bar labels)

import { formatDate } from '@platform/app-shell/frontend/utils/dateFormat';
// Sort keys live in sortUtils (no alias imports) so node suites can load them.
import { getSortableValue, getNumericFieldValue, compareSortValues, sortIssues } from './sortUtils.js';
import { formatNumberDisplay } from './fieldFormatter.js';

// Jira returns date custom fields as ISO date strings (`2026-11-16`) and
// datetime fields as full ISO timestamps (`2026-11-16T13:45:00.000+0000`).
// We detect these by shape and route them through formatDate so they
// follow the admin's chosen format. Anything else is left as-is.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const formatIfIsoDate = (value) => {
  if (typeof value !== 'string') return null;
  if (ISO_DATETIME_RE.test(value)) return formatDate(value, 'datetime');
  if (ISO_DATE_RE.test(value)) return formatDate(value, 'date');
  return null;
};

const COMMON_FIELDS = [
  { id: 'key', name: 'Key' },
  { id: 'summary', name: 'Summary' },
  { id: 'status', name: 'Status' },
  { id: 'assignee', name: 'Assignee' },
  { id: 'reporter', name: 'Reporter' },
  { id: 'priority', name: 'Priority' },
  { id: 'issuetype', name: 'Work Item Type' },
  { id: 'created', name: 'Created' },
  { id: 'updated', name: 'Updated' },
  { id: 'duedate', name: 'Due Date' },
  { id: 'statuscategorychangedate', name: 'Status Category Changed' },
  { id: 'labels', name: 'Labels' },
  { id: 'components', name: 'Components' },
  { id: 'fixVersions', name: 'Fix Versions' },
  { id: 'resolution', name: 'Resolution' },
  { id: 'project', name: 'Project' },
  { id: 'projectCategory', name: 'Project Category' },
  { id: 'timeoriginalestimate', name: 'Original Estimate' },
  { id: 'timeestimate', name: 'Remaining Estimate' },
  { id: 'timespent', name: 'Time Spent' },
];

// Atlassian Document Format (ADF) — the JSON shape Jira stores rich-text /
// "wiki" fields in (description and custom Paragraph fields). A cell was showing
// the raw `{"type":"doc",…}` JSON; flatten it to readable plain text instead.
// Text nodes contribute their text; block nodes (paragraph, list item, heading…)
// end with a newline so they don't run together; hard breaks become newlines.
const adfToPlainText = (node) => {
  if (node == null) return '';
  if (Array.isArray(node)) return node.map(adfToPlainText).join('');
  if (typeof node !== 'object') return String(node);
  if (node.type === 'text') return typeof node.text === 'string' ? node.text : '';
  if (node.type === 'hardBreak' || node.type === 'rule') return '\n';
  if (node.type === 'mention' || node.type === 'emoji') return node.attrs?.text || node.attrs?.shortName || '';
  if (node.type === 'inlineCard' || node.type === 'blockCard') return node.attrs?.url || '';
  const inner = Array.isArray(node.content) ? node.content.map(adfToPlainText).join('') : '';
  const BLOCK = new Set(['paragraph', 'heading', 'blockquote', 'listItem', 'codeBlock', 'panel', 'tableRow']);
  return BLOCK.has(node.type) ? inner + '\n' : inner;
};
const isAdfDoc = (v) => v && typeof v === 'object' && v.type === 'doc' && Array.isArray(v.content);
// Returns the flattened text if the value is an ADF doc (object or its JSON
// string), else null so the caller keeps its normal handling.
const formatIfAdf = (value) => {
  let doc = null;
  if (isAdfDoc(value)) doc = value;
  else if (typeof value === 'string') {
    const s = value.trim();
    if (s.startsWith('{') && s.includes('"doc"')) {
      try { const p = JSON.parse(s); if (isAdfDoc(p)) doc = p; } catch { /* not JSON — leave as null */ }
    }
  }
  if (!doc) return null;
  return adfToPlainText(doc).replace(/\n{2,}/g, '\n').trim();
};

// Format seconds into human-readable time (e.g., 3600 → "1h", 5400 → "1h 30m")
// Also handles string values (pass-through from local updates after editing)
const formatTimeSeconds = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value; // Already formatted (local update)
  const seconds = Number(value);
  if (isNaN(seconds)) return '';
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  if (mins > 0) return `${mins}m`;
  return '0m';
};

// Get display value from issue based on field ID
const getFieldValue = (issue, fieldId) => {
  if (!issue) return '';

  switch (fieldId) {
    case 'key':
      return issue.key;
    case 'summary':
      return issue.fields?.summary || '';
    case 'status':
      return issue.fields?.status?.name || '';
    case 'assignee':
      return issue.fields?.assignee?.displayName || 'Unassigned';
    case 'reporter':
      return issue.fields?.reporter?.displayName || '';
    case 'priority':
      return issue.fields?.priority?.name || '';
    case 'issuetype':
      return issue.fields?.issuetype?.name || '';
    case 'created':
      return formatDate(issue.fields?.created, 'date');
    case 'updated':
      return formatDate(issue.fields?.updated, 'date');
    case 'duedate':
      return formatDate(issue.fields?.duedate, 'date');
    case 'statuscategorychangedate':
      return formatDate(issue.fields?.statuscategorychangedate, 'datetime');
    case 'labels':
      return issue.fields?.labels?.join(', ') || '';
    case 'components':
      return issue.fields?.components?.map(c => c.name).join(', ') || '';
    case 'fixVersions':
      return issue.fields?.fixVersions?.map(v => v.name).join(', ') || '';
    case 'resolution':
      return issue.fields?.resolution?.name || '';
    case 'project':
      return issue.fields?.project?.name || '';
    case 'projectCategory':
      return issue.fields?.project?.projectCategory?.name || '';
    case 'timeoriginalestimate':
      return formatTimeSeconds(issue.fields?.timeoriginalestimate);
    case 'timeestimate':
      return formatTimeSeconds(issue.fields?.timeestimate);
    case 'timespent':
      return formatTimeSeconds(issue.fields?.timespent);
    case 'parent':
      return issue.fields?.parent?.key || '';
    default:
      // Custom field
      if (fieldId?.startsWith('customfield_')) {
        const value = issue.fields?.[fieldId];
        if (value === null || value === undefined) return '';
        // Rich-text / "wiki" fields arrive as ADF — show their text, not raw JSON.
        const adfText = formatIfAdf(value);
        if (adfText !== null) return adfText;
        if (Array.isArray(value)) {
          return value
            .filter(v => v != null)
            .map(v =>
              typeof v === 'object' ? (v.value || v.name || v.displayName || JSON.stringify(v)) : String(v)
            ).join(', ');
        }
        if (typeof value === 'object') {
          return value.value || value.name || value.displayName || JSON.stringify(value);
        }
        const formattedDate = formatIfIsoDate(value);
        if (formattedDate !== null) return formattedDate;
        // Number fields hydrate as JS numbers (transformers.js) — group them.
        if (typeof value === 'number') return formatNumberDisplay(value);
        return String(value);
      }
      return '';
  }
};

export { COMMON_FIELDS, getFieldValue, formatTimeSeconds, getSortableValue, getNumericFieldValue, compareSortValues, sortIssues, formatIfAdf };
